import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import EmptyState from "@/components/ui/EmptyState";
import { Field, Select, Checkbox } from "@/components/ui/Field";
import { Link, router } from "@inertiajs/react";
import type { Submission } from "@/interfaces/submission";

type Shipping = {
  first_name: string | null;
  last_name: string | null;
  birthday: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_country: string | null;
  complete: boolean;
};

type Row = Submission & {
  event_name: string | null;
  event_slug: string | null;
  shipping: Shipping | null;
  airtable_synced_at: string | null;
  airtable_sync_error: string | null;
};

// Every site handed in, across every event, with the parcel details behind it.
// HQ only - organizers never see this, which is why it isn't on their dashboard.
export default function AdminSubmissions({
  submissions,
  events,
  filters,
  total_count,
  truncated,
  ready_to_ship,
  airtable_pending,
}: {
  submissions: Row[];
  events: { id: number; name: string }[];
  filters: { event_id: number | null; missing_shipping: boolean };
  total_count: number;
  truncated: boolean;
  ready_to_ship: number;
  airtable_pending: number;
}) {
  const applyFilter = (next: Record<string, string | undefined>) => {
    router.get("/admin/submissions", next, {
      preserveState: true,
      replace: true,
    });
  };

  return (
    <Layout>
      <div className="flex w-full flex-col gap-6 py-6">
        <PageHeading
          title="Hand-ins"
          subtitle="Every site built at a Boba Bash, and where to post their stuff."
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Panel title="handed-in.count">
            <p className="font-display text-display text-ink font-bold">
              {total_count}
            </p>
          </Panel>
          <Panel title="postable.count">
            <p className="font-display text-display text-ink font-bold">
              {ready_to_ship}
            </p>
            <p className="text-caption text-ink-soft">
              Have a name, a date of birth and an address.
            </p>
          </Panel>
          <Panel title="airtable.queue">
            <p className="font-display text-display text-ink font-bold">
              {airtable_pending}
            </p>
            <p className="text-caption text-ink-soft">
              Waiting to reach the Ships table.
            </p>
          </Panel>
        </div>

        <Panel title="filters.txt" tone="alt">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-56">
              <Field label="Event">
                <Select
                  value={filters.event_id ?? ""}
                  onChange={(e) =>
                    applyFilter({
                      event_id: e.target.value || undefined,
                      missing_shipping: filters.missing_shipping
                        ? "1"
                        : undefined,
                    })
                  }
                >
                  <option value="">Every event</option>
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <label className="flex cursor-pointer items-center gap-2 pb-2">
              <Checkbox
                checked={filters.missing_shipping}
                onChange={(e) =>
                  applyFilter({
                    event_id: filters.event_id
                      ? String(filters.event_id)
                      : undefined,
                    missing_shipping: e.target.checked ? "1" : undefined,
                  })
                }
              />
              <span className="text-body text-ink">
                Only ones we can't post to
              </span>
            </label>
          </div>
        </Panel>

        {truncated && (
          <p className="text-caption text-ink-soft">
            Showing the newest 500 of {total_count}. Narrow it with the filters
            above.
          </p>
        )}

        {submissions.length === 0 ? (
          <EmptyState
            title="Nothing handed in yet"
            description="Sites show up here as attendees hand them in."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {submissions.map((row) => (
              <SubmissionRow key={row.id} row={row} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function SubmissionRow({ row }: { row: Row }) {
  const shipping = row.shipping;

  return (
    <Panel title={`${row.event_name ?? "event"} / ${row.attendee_email ?? ""}`}>
      <div className="flex flex-col gap-3 md:flex-row md:gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display text-subheading text-ink font-bold">
              {row.title}
            </span>
            {row.removed && <Badge tone="danger">Taken down</Badge>}
            {!shipping?.complete && (
              <Badge tone="warning">Can't post to this</Badge>
            )}
            {row.airtable_sync_error && (
              <Badge tone="danger">Airtable error</Badge>
            )}
          </div>
          <a
            href={row.live_url}
            target="_blank"
            rel="noopener noreferrer nofollow ugc"
            className="text-link text-caption break-all underline"
          >
            {row.live_url}
          </a>
          {row.description && (
            <p className="text-caption text-ink-muted">{row.description}</p>
          )}
          {row.event_slug && (
            <Link
              href={`/events/${row.event_slug}/submissions`}
              className="text-caption text-ink-soft underline"
            >
              See it in the gallery
            </Link>
          )}
          {row.airtable_sync_error && (
            <p className="text-caption text-sunset-orange">
              {row.airtable_sync_error}
            </p>
          )}
        </div>

        <div className="text-caption text-ink-muted flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="font-display text-ink font-bold">Post it to</span>
          {shipping?.complete ? (
            <>
              <span>
                {shipping.first_name} {shipping.last_name}
              </span>
              <span>{shipping.address_line_1}</span>
              {shipping.address_line_2 && (
                <span>{shipping.address_line_2}</span>
              )}
              <span>
                {[
                  shipping.address_city,
                  shipping.address_state,
                  shipping.address_zip,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
              <span>{shipping.address_country}</span>
              <span className="text-ink-soft">Born {shipping.birthday}</span>
            </>
          ) : (
            <span className="text-ink-soft">
              They handed in before the address form existed, or left it blank.
            </span>
          )}
        </div>
      </div>
    </Panel>
  );
}
